import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import Papa from "papaparse";
import {
  ArrowRight, Download, FileText, Loader2, Plus, Upload, X, AlertTriangle, CheckCircle2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableHead, TableHeader, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { PageHeader } from "@/components/ui/page-header";
import { useScope } from "@/hooks/use-scope";
import { supabase } from "@/integrations/supabase/client";

import {
  listEntities, listImports, getImport, listImportRows,
  createImport, setImportMapping, commitImport, cancelImport,
  suggestMapping, getErrorsCsv,
} from "@/lib/imports.functions";

export const Route = createFileRoute("/_authenticated/app/imports")({
  component: ImportsPage,
});

type ImportRow = {
  id: string; entity: string; source_filename: string; source_format: string;
  status: string; dry_run: boolean;
  row_count: number; ok_count: number; error_count: number; committed_count: number;
  error_summary: string | null; created_at: string;
  started_at: string | null; finished_at: string | null;
};

const ACTIVE = ["parsing", "validating", "committing"];

function StatusBadge({ status, dryRun }: { status: string; dryRun: boolean }) {
  const map: Record<string, string> = {
    awaiting_mapping: "bg-muted text-muted-foreground",
    parsing: "bg-info/10 text-info border-info/20",
    validating: "bg-info/10 text-info border-info/20",
    validated: "bg-warning/10 text-warning border-warning/20",
    committing: "bg-info/10 text-info border-info/20",
    completed: "bg-success/10 text-success border-success/20",
    failed: "bg-destructive/10 text-destructive border-destructive/20",
    cancelled: "bg-muted text-muted-foreground",
  };
  return (
    <div className="flex items-center gap-1.5">
      <Badge variant="outline" className={map[status] ?? ""}>
        {status.replace(/_/g, " ")}
      </Badge>
      {dryRun && status === "validated" && (
        <Badge variant="outline" className="text-xs">dry run</Badge>
      )}
    </div>
  );
}

function ImportsPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listImports);
  const cancelFn = useServerFn(cancelImport);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["imports", tenantId],
    queryFn: () => listFn({ data: { tenantId: tenantId!, limit: 100 } }),
    enabled: !!tenantId,
    refetchInterval: (q) => {
      const rows = (q.state.data as { rows: ImportRow[] } | undefined)?.rows ?? [];
      return rows.some((r) => ACTIVE.includes(r.status)) ? 3000 : false;
    },
  });

  const cancelMut = useMutation({
    mutationFn: (importId: string) => cancelFn({ data: { importId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["imports", tenantId] }),
  });

  if (!tenantId) {
    return <div className="p-6 text-sm text-muted-foreground">Select a tenant to manage imports.</div>;
  }

  const rows = (data?.rows ?? []) as ImportRow[];

  // Staleness detection: an import stuck in an ACTIVE state for >2 minutes
  // almost always means the cron processor is not running (or mis-wired).
  // Surface a single banner so operators don't think the system is broken.
  const stalled = rows.filter((r) => {
    if (!ACTIVE.includes(r.status)) return false;
    const startedMs = r.started_at ? new Date(r.started_at).getTime() : new Date(r.created_at).getTime();
    return Date.now() - startedMs > 2 * 60 * 1000;
  });

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <PageHeader
        title="Imports"
        description="Upload CSV or JSON files into any core entity. Map columns, dry-run, then commit."
        actions={
          <Button onClick={() => setWizardOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> New import
          </Button>
        }
      />

      {stalled.length > 0 && (
        <Card className="p-3 border-warning/40 bg-warning/5">
          <div className="flex items-start gap-3 text-sm">
            <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-warning-foreground">
                {stalled.length} import{stalled.length === 1 ? "" : "s"} stalled
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Status hasn't advanced in over 2 minutes. The import processor (cron) may not be running.
                Imports are picked up by <code className="text-xs">/api/public/cron/process-imports</code> —
                verify the scheduled job is active. Cancel and retry once the processor is healthy.
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Entity</TableHead>
              <TableHead>File</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Rows</TableHead>
              <TableHead className="text-right">OK</TableHead>
              <TableHead className="text-right">Errors</TableHead>
              <TableHead className="text-right">Committed</TableHead>
              <TableHead>When</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
              </TableCell></TableRow>
            )}
            {!isLoading && rows.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground text-sm">
                No imports yet. Click "New import" to upload your first file.
              </TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} className="cursor-pointer" onClick={() => setDetailId(r.id)}>
                <TableCell className="font-mono text-xs">{r.entity}</TableCell>
                <TableCell className="max-w-[240px] truncate text-sm">
                  <FileText className="h-3.5 w-3.5 inline mr-1.5 text-muted-foreground" />
                  {r.source_filename}
                </TableCell>
                <TableCell><StatusBadge status={r.status} dryRun={r.dry_run} /></TableCell>
                <TableCell className="text-right tabular-nums">{r.row_count}</TableCell>
                <TableCell className="text-right tabular-nums text-success">{r.ok_count}</TableCell>
                <TableCell className="text-right tabular-nums text-destructive">{r.error_count || ""}</TableCell>
                <TableCell className="text-right tabular-nums">{r.committed_count || ""}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {ACTIVE.includes(r.status) && (
                    <Button size="sm" variant="ghost" aria-label="Cancel import" onClick={() => cancelMut.mutate(r.id)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <ImportWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        tenantId={tenantId}
        onCreated={() => { setWizardOpen(false); qc.invalidateQueries({ queryKey: ["imports", tenantId] }); }}
      />

      <ImportDetail
        importId={detailId}
        onClose={() => setDetailId(null)}
        tenantId={tenantId}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wizard
// ---------------------------------------------------------------------------

type Entity = { entity: string; label: string; fields: Array<{ key: string; label: string; type: string; required: boolean; aliases?: string[]; enumValues?: string[]; description?: string }> };

function sampleValueFor(f: Entity["fields"][number]): string {
  if (f.enumValues && f.enumValues.length) return f.enumValues[0];
  switch (f.type) {
    case "number":
    case "integer":
    case "int":
      return "0";
    case "boolean":
    case "bool":
      return "true";
    case "date":
    case "datetime":
    case "timestamp":
      return new Date().toISOString().slice(0, 10);
    case "uuid":
      return "";
    case "json":
    case "object":
      return "{}";
    default:
      return `example_${f.key}`;
  }
}

function downloadCsvTemplate(adapter: Entity) {
  const cols = adapter.fields.map((f) => f.key);
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const header = cols.join(",");
  const sample = adapter.fields.map((f) => esc(sampleValueFor(f))).join(",");
  const blob = new Blob([header + "\n" + sample + "\n"], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${adapter.entity}_template.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function ImportWizard({
  open, onClose, tenantId, onCreated,
}: { open: boolean; onClose: () => void; tenantId: string; onCreated: () => void }) {
  const [step, setStep] = useState<1 | 2>(1);
  const [entity, setEntity] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [samples, setSamples] = useState<Record<string, string[]>>({});
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const entitiesFn = useServerFn(listEntities);
  const createFn = useServerFn(createImport);
  const setMappingFn = useServerFn(setImportMapping);
  const suggestFn = useServerFn(suggestMapping);

  const { data: entitiesData } = useQuery({
    queryKey: ["import-entities"],
    queryFn: () => entitiesFn({}),
    enabled: open,
  });
  const entities: Entity[] = (entitiesData?.entities ?? []) as Entity[];
  const adapter = entities.find((e) => e.entity === entity);

  useEffect(() => {
    if (!open) {
      setStep(1); setEntity(""); setFile(null); setHeaders([]); setSamples({}); setMapping({});
    }
  }, [open]);

  const onFile = async (f: File) => {
    setFile(f);
    const text = await f.text();
    if (f.name.toLowerCase().endsWith(".json")) {
      try {
        const arr = JSON.parse(text);
        if (!Array.isArray(arr) || arr.length === 0) { toast.error("JSON must be a non-empty array"); return; }
        const keys = Object.keys(arr[0]);
        setHeaders(keys);
        const s: Record<string, string[]> = {};
        for (const k of keys) s[k] = arr.slice(0, 5).map((r) => String((r as Record<string, unknown>)[k] ?? ""));
        setSamples(s);
      } catch { toast.error("Invalid JSON file"); return; }
    } else {
      const res = Papa.parse<Record<string, string>>(text, { header: true, preview: 5 });
      const fields = res.meta.fields ?? [];
      setHeaders(fields);
      const s: Record<string, string[]> = {};
      for (const k of fields) s[k] = res.data.map((r) => String(r[k] ?? ""));
      setSamples(s);
    }
  };

  const goToStep2 = async () => {
    if (!entity || !file || headers.length === 0) { toast.error("Pick an entity and a file"); return; }
    const { mapping: m } = await suggestFn({ data: { tenantId, entity, headers } });
    setMapping(m);
    setStep(2);
  };

  const submit = async (dryRun: boolean) => {
    if (!file || !adapter) return;
    const missingRequired = adapter.fields.filter((f) => f.required && !Object.values(mapping).includes(f.key));
    if (missingRequired.length) {
      toast.error(`Required fields not mapped: ${missingRequired.map((f) => f.label).join(", ")}`);
      return;
    }
    setSubmitting(true);
    try {
      const format = file.name.toLowerCase().endsWith(".json") ? "json" : "csv";
      const created = await createFn({
        data: { tenantId, entity, filename: file.name, format },
      });
      // Upload file directly to storage via signed URL
      const { error: upErr } = await supabase.storage
        .from("tenant-imports")
        .uploadToSignedUrl(created.path, created.token, file, {
          contentType: format === "json" ? "application/json" : "text/csv",
        });
      if (upErr) throw new Error(upErr.message);
      await setMappingFn({ data: { importId: created.importId, mapping, dryRun } });
      toast.success(dryRun ? "Queued for validation" : "Queued for import");
      onCreated();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New import · Step {step} of 2</DialogTitle>
          <DialogDescription>
            {step === 1 ? "Pick an entity and upload a CSV or JSON file (max 25 MB)." : "Map your file's columns to entity fields, then run validation."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label>Entity</Label>
              <Select value={entity} onValueChange={setEntity}>
                <SelectTrigger><SelectValue placeholder="Pick an entity" /></SelectTrigger>
                <SelectContent>
                  {entities.map((e) => (
                    <SelectItem key={e.entity} value={e.entity}>{e.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {adapter && (
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {adapter.fields.length} fields · {adapter.fields.filter((f) => f.required).length} required
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => downloadCsvTemplate(adapter)}
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Download template
                  </Button>
                </div>
              )}
            </div>
            <div>
              <Label>File</Label>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) void onFile(f); }}
                className="border-2 border-dashed rounded-md p-8 text-center cursor-pointer hover:bg-muted/30 transition"
              >
                {file ? (
                  <div className="text-sm">
                    <FileText className="h-5 w-5 inline mr-2" />
                    {file.name} · {(file.size / 1024).toFixed(1)} KB · {headers.length} columns detected
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    <Upload className="h-5 w-5 inline mr-2" />
                    Drop a .csv or .json file, or click to browse
                  </div>
                )}
                <input
                  ref={fileRef} type="file" accept=".csv,.json,text/csv,application/json"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={goToStep2} disabled={!entity || !file || headers.length === 0}>
                Next <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && adapter && (
          <div className="space-y-4">
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[35%]">File column</TableHead>
                    <TableHead className="w-[35%]">Maps to field</TableHead>
                    <TableHead>Sample</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {headers.map((h) => (
                    <TableRow key={h}>
                      <TableCell className="font-mono text-xs">{h}</TableCell>
                      <TableCell>
                        <Select
                          value={mapping[h] ?? "__skip__"}
                          onValueChange={(v) => setMapping((m) => {
                            const next = { ...m };
                            if (v === "__skip__") delete next[h]; else next[h] = v;
                            return next;
                          })}
                        >
                          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__skip__">— Skip —</SelectItem>
                            {adapter.fields.map((f) => (
                              <SelectItem key={f.key} value={f.key}>
                                {f.label}{f.required ? " *" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate">
                        {(samples[h] ?? []).filter(Boolean).slice(0, 3).join(" · ")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <div className="text-xs text-muted-foreground">
              Required fields: {adapter.fields.filter((f) => f.required).map((f) => f.label).join(", ") || "none"}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button variant="outline" onClick={() => submit(false)} disabled={submitting}>
                Skip dry-run · Commit now
              </Button>
              <Button onClick={() => submit(true)} disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Validate (dry-run)
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Detail sheet
// ---------------------------------------------------------------------------

function ImportDetail({
  importId, onClose, tenantId,
}: { importId: string | null; onClose: () => void; tenantId: string }) {
  const qc = useQueryClient();
  const getFn = useServerFn(getImport);
  const rowsFn = useServerFn(listImportRows);
  const commitFn = useServerFn(commitImport);
  const errCsvFn = useServerFn(getErrorsCsv);

  const { data: impData } = useQuery({
    queryKey: ["import", importId],
    queryFn: () => getFn({ data: { importId: importId! } }),
    enabled: !!importId,
    refetchInterval: (q) => {
      const r = (q.state.data as { row: ImportRow | null } | undefined)?.row;
      return r && ACTIVE.includes(r.status) ? 2500 : false;
    },
  });

  const imp = impData?.row as ImportRow | null | undefined;

  const { data: rowsData } = useQuery({
    queryKey: ["import-rows", importId, "error"],
    queryFn: () => rowsFn({ data: { importId: importId!, status: "error", limit: 100 } }),
    enabled: !!importId && !!imp && imp.error_count > 0,
  });

  const commitMut = useMutation({
    mutationFn: () => commitFn({ data: { importId: importId! } }),
    onSuccess: () => {
      toast.success("Commit queued");
      qc.invalidateQueries({ queryKey: ["import", importId] });
      qc.invalidateQueries({ queryKey: ["imports", tenantId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const downloadErrors = async () => {
    const { csv } = await errCsvFn({ data: { importId: importId! } });
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `import-${importId}-errors.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Sheet open={!!importId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-2xl w-full overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{imp ? imp.source_filename : "Import"}</SheetTitle>
        </SheetHeader>

        {imp && (
          <div className="space-y-4 mt-4">
            <div className="flex items-center gap-2">
              <StatusBadge status={imp.status} dryRun={imp.dry_run} />
              <Badge variant="outline" className="font-mono text-xs">{imp.entity}</Badge>
              <Badge variant="outline" className="text-xs">{imp.source_format.toUpperCase()}</Badge>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {[
                { label: "Rows", value: imp.row_count },
                { label: "OK", value: imp.ok_count, tone: "text-success" },
                { label: "Errors", value: imp.error_count, tone: imp.error_count ? "text-destructive" : "" },
                { label: "Committed", value: imp.committed_count },
              ].map((k) => (
                <Card key={k.label} className="p-3">
                  <div className="text-xs text-muted-foreground">{k.label}</div>
                  <div className={`text-xl font-semibold tabular-nums ${k.tone ?? ""}`}>{k.value}</div>
                </Card>
              ))}
            </div>

            {imp.error_summary && (
              <div className="text-sm p-3 rounded border border-destructive/30 bg-destructive/5 text-destructive">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                {imp.error_summary}
              </div>
            )}

            {imp.status === "validated" && (
              <Card className="p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success mt-0.5" />
                  <div className="text-sm">
                    Dry-run complete. {imp.ok_count} rows ready to commit
                    {imp.error_count > 0 ? `, ${imp.error_count} will be skipped` : ""}.
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => commitMut.mutate()} disabled={imp.ok_count === 0 || commitMut.isPending}>
                    {commitMut.isPending && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
                    Commit {imp.ok_count} rows
                  </Button>
                  {imp.error_count > 0 && (
                    <Button size="sm" variant="outline" onClick={downloadErrors}>
                      <Download className="h-3.5 w-3.5 mr-2" /> Download errors CSV
                    </Button>
                  )}
                </div>
              </Card>
            )}

            {imp.error_count > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Failed rows</Label>
                  <Button size="sm" variant="outline" onClick={downloadErrors}>
                    <Download className="h-3.5 w-3.5 mr-2" /> Errors CSV
                  </Button>
                </div>
                <Card className="overflow-hidden max-h-[40vh] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Row</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(rowsData?.rows ?? []).map((r: { id: string; row_number: number; error: string | null }) => (
                        <TableRow key={r.id}>
                          <TableCell className="font-mono text-xs">{r.row_number}</TableCell>
                          <TableCell className="text-xs text-destructive">{r.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}